<template>
  <div class="icon-wrapper">
    <svg
      v-if="svgPaths.length" v-bind="svgAttrs" :viewBox="viewBox" :style="iconStyle.style"
      :class="iconStyle.class"
    >
      <path v-for="(path, index) in svgPaths" :key="index" v-bind="path.attrs" />
    </svg>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import * as icons from '@/components/common/icons/index'

export interface IconOptions {
  name: string
  iconHeight?: number
  fill?: string
  stroke?: string
  disabled?: boolean
}

const props = defineProps<IconOptions>()

const svgPaths = ref<Array<{ attrs: Record<string, string> }>>([])
const viewBox = ref('')
const svgAttrs = ref<Record<string, string>>({})

const updatePathColors = () => {
  svgPaths.value = svgPaths.value.map((path) => {
    const editPath = { ...path }
    if (path.attrs.fill && props.fill) {
      editPath.attrs.fill = props.fill
    }
    if (path.attrs.stroke && props.stroke) {
      editPath.attrs.stroke = props.stroke
    }
    return editPath
  })
}

const loadSvg = async () => {
  const iconModule = icons[props.name as keyof typeof icons]
  if (iconModule) {
    const response = await fetch(iconModule)
    const svgText = await response.text()

    const viewBoxMatch = svgText.match(/viewBox=['"]([^'"]*)['"]/)?.[1]
    if (viewBoxMatch) {
      viewBox.value = viewBoxMatch
    } else {
      viewBox.value = '0 0 24 24'
    }

    const parser = new DOMParser()
    const svgDoc = parser.parseFromString(svgText, 'image/svg+xml')

    const svgElement = svgDoc.querySelector('svg')
    if (svgElement) {
      svgAttrs.value = Array.from(svgElement.attributes).reduce(
        (acc, attr) => {
          acc[attr.name] = attr.value
          return acc
        },
        {} as Record<string, string>
      )

      if (!svgAttrs.value.viewBox) {
        svgAttrs.value.viewBox = '0 0 24 24'
      }
    }

    const paths = Array.from(svgDoc.querySelectorAll('path'))
    svgPaths.value = paths.map((path) => {
      const attrs = Array.from(path.attributes).reduce(
        (acc, attr) => {
          acc[attr.name] = attr.value
          return acc
        },
        {} as Record<string, string>
      )
      return { attrs }
    })

    if (!svgPaths.value.length) {
      console.warn('SVG has no paths:', props.name)
    }

    updatePathColors()
  }
}

onMounted(loadSvg)

watch(() => props.name, loadSvg)
watch([() => props.fill, () => props.stroke], updatePathColors)

const iconStyle = computed(() => {
  const inlineStyle: Record<string, string> = {}
  const classes: string[] = []
  if (props.disabled) {
    classes.push('disabled')
  }
  if (props.iconHeight) {
    inlineStyle.height = `${props.iconHeight}px`
    inlineStyle.width = `${props.iconHeight}px`
  } else {
    inlineStyle.height = '24px' // default
    inlineStyle.width = '24px' // default
  }
  return { style: inlineStyle, class: classes }
})
</script>

<style scoped>
.icon-wrapper {
  display: flex;
  justify-self: center;
}
</style>
